import { useCallback, useSyncExternalStore } from "react";

/**
 * Any route can be starred from the header. The sidebar lists the pages
 * in one Starred group. Local storage is per browser, like starred chats.
 */
export type StarredPage = { path: string; label: string; context: string | null };

const STORAGE_KEY = "bastion.starredPages";
const listeners = new Set<() => void>();
let cache: StarredPage[] | null = null;

function load(): StarredPage[] {
  if (cache) return cache;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    cache = raw ? (JSON.parse(raw) as StarredPage[]) : [];
  } catch {
    cache = [];
  }
  return cache;
}

function save(pages: StarredPage[]) {
  cache = pages;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pages));
  } catch {
    // The stars last for this visit.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useStarredPages() {
  const starred = useSyncExternalStore(subscribe, load, () => []);
  const isStarred = useCallback(
    (path: string) => starred.some((page) => page.path === path),
    [starred],
  );
  const toggleStar = useCallback((page: StarredPage) => {
    const pages = load();
    save(
      pages.some((p) => p.path === page.path)
        ? pages.filter((p) => p.path !== page.path)
        : [page, ...pages],
    );
  }, []);
  return { starred, isStarred, toggleStar };
}
